import type { PropsWithChildren } from "react"
import { createContext, useContext } from "react"

import type { AugmentedFeedback } from "@productlane/api/src/lib/timeline-events"
import type { ThreadTimelineCursor } from "@productlane/zero/queries"

import type { QueryResultDetails } from "@rocicorp/zero"

export type ThreadTab = "timeline" | "thread"

export interface ExtractedLink {
	url: string
	text: string
	id: string
}

export interface ThreadContextState {
	thread: AugmentedFeedback

	goNext: () => void
	goPrev: () => void
	nextThread: { id: string } | null
	prevThread: { id: string } | null

	changeTab: (tab: ThreadTab) => void
	tab: ThreadTab

	isReply: boolean
	setIsReply: (isReply: boolean) => void
	isComment: boolean
	setIsComment: (isComment: boolean) => void

	queryResult: QueryResultDetails

	baseLimit: number
	pages: ThreadTimelineCursor[]
	loadOlderMessages: (cursor: ThreadTimelineCursor) => void
	growBaseLimit: () => void

	extractedLinks: ExtractedLink[]
	setExtractedLinks: (links: ExtractedLink[]) => void

	/** Off where E means something else, like deleting on the notifications page. */
	doneShortcutEnabled?: boolean

	/** Off where the hosting page owns Esc, like clearing the Agent page's selection. */
	leaveOnEscape?: boolean
	/** On the Agent page's Chats, E marks the chat reviewed instead of done. */
	reviewMode?: boolean
	/** Where the thread lives on the hosting page, for undo and the last item. */
	threadPath?: (threadId: string) => string
	exitPath?: string
}

export const ThreadContext = createContext<ThreadContextState | null>(null)

export function ThreadContextProvider({
	value,
	children,
}: PropsWithChildren<{ value: ThreadContextState }>) {
	return (
		<ThreadContext.Provider value={value}>{children}</ThreadContext.Provider>
	)
}
export { isThreadNoteEditable } from "./thread-note-editable"

export const useThreadContext = () => {
	const context = useContext(ThreadContext)
	if (!context) {
		throw Error("Wrap your modal with `<ThreadContextProvider />`")
	} else {
		return context
	}
}
